import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, CheckCircle2, AlertTriangle, Pencil, Users } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../../components/ui/Tabs';
import StickyActionBar from '../../../../components/ui/StickyActionBar';
import { PageLayout } from '../../../../components/ui/PageLayout';
import {
    getCurrentTerms,
    getTermsDraft,
    updateTermsDraft,
    publishTerms,
} from '../../../../api/General/termsAndPolicies';
import type { TermsAndPoliciesView } from '../../../../models/general';
import AcceptanceTab from './AcceptanceTab';

export default function TermsAndPoliciesSetup() {
    const { t } = useTranslation();

    const [published, setPublished] = useState<TermsAndPoliciesView | null>(null);
    const [draft, setDraft] = useState<TermsAndPoliciesView | null>(null);
    const [content, setContent] = useState<string>('');
    const [savedContent, setSavedContent] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
    const [activeTab, setActiveTab] = useState<'editor' | 'acceptance'>('editor');

    // StickyActionBar belongs to the editor only; gate it so it doesn't
    // appear on the acceptance tab if a stray draft edit is in flight.
    const hasUnsavedChanges = activeTab === 'editor' && content !== savedContent;

    async function refresh() {
        setLoading(true);
        try {
            const [cur, drf] = await Promise.all([getCurrentTerms(), getTermsDraft()]);
            setPublished(cur.payload ?? null);
            setDraft(drf.payload);
            setContent(drf.payload?.content ?? '');
            setSavedContent(drf.payload?.content ?? '');
        } catch {
            setMessage({ kind: 'error', text: t('Failed to load terms and policies') });
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { refresh(); }, []);

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const res = await updateTermsDraft({ content });
            if (res.errors?.length) {
                setMessage({ kind: 'error', text: res.errors[0].description ?? t('Failed to save draft') });
            } else {
                setDraft(res.payload);
                setSavedContent(res.payload.content);
                setMessage({ kind: 'success', text: t('Draft saved') });
            }
        } catch {
            setMessage({ kind: 'error', text: t('Failed to save draft') });
        } finally {
            setSaving(false);
        }
    };

    const handlePublish = async () => {
        if (hasUnsavedChanges) {
            await handleSave();
        }
        setPublishing(true);
        setMessage(null);
        try {
            const res = await publishTerms();
            if (res.errors?.length) {
                setMessage({ kind: 'error', text: res.errors[0].description ?? t('Failed to publish') });
            } else {
                setMessage({ kind: 'success', text: t('Terms & Policies published — all employees must re-accept') });
                await refresh();
            }
        } catch {
            setMessage({ kind: 'error', text: t('Failed to publish') });
        } finally {
            setPublishing(false);
            setConfirmOpen(false);
        }
    };

    const handleReset = () => setContent(savedContent);

    const formattedDate = useMemo(
        () => (published?.publishedAt ? new Date(published.publishedAt).toLocaleString() : null),
        [published?.publishedAt]
    );

    return (
        <PageLayout
            title={t('Terms & Policies')}
            description={t('Author the company\'s Terms & Policies. Once published, employees must accept them before accessing the system.')}
            icon={FileText}
            accentColor="slate"
            maxWidth="5xl"
        >
            <div className="flex flex-col gap-6">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'editor' | 'acceptance')}>
                <TabsList>
                    <TabsTrigger value="editor" className="flex items-center gap-2" data-testid="tnp-tab-editor">
                        <Pencil className="w-4 h-4" />
                        {t('Editor')}
                    </TabsTrigger>
                    <TabsTrigger value="acceptance" className="flex items-center gap-2" data-testid="tnp-tab-acceptance">
                        <Users className="w-4 h-4" />
                        {t('Acceptance')}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="editor" className="flex flex-col gap-6 mt-4">
            {/* Current published card */}
            <div className="bg-white rounded-lg border border-border p-4 flex flex-wrap items-center gap-4 justify-between">
                <div className="flex items-center gap-3">
                    <CheckCircle2 className={`w-5 h-5 ${published ? 'text-green-600' : 'text-gray-300'}`} />
                    <div>
                        <div className="text-sm font-medium">{t('Current published version')}</div>
                        <div className="text-xs text-gray-500" data-testid="terms-current-version">
                            {published ? `v${published.version}${formattedDate ? ` — ${formattedDate}` : ''}` : t('Not published yet')}
                        </div>
                    </div>
                </div>
                <div className="text-sm text-gray-600">
                    {t('Working draft')}: <span className="font-medium" data-testid="terms-draft-version">v{draft?.version ?? '…'}</span>
                </div>
            </div>

            {/* Editor */}
            <div className="bg-white rounded-lg border border-border">
                <div className="p-4 border-b border-border flex items-center justify-between">
                    <h2 className="text-sm font-medium">{t('Draft content')}</h2>
                    <span className="text-xs text-gray-500">{content.length} {t('characters')}</span>
                </div>
                <textarea
                    data-testid="terms-draft-textarea"
                    className="w-full min-h-[400px] p-4 text-sm font-mono leading-relaxed resize-y focus:outline-none"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    disabled={loading}
                    placeholder={t('Write the company\'s terms and policies here…')}
                />
            </div>

            {message && (
                <div
                    data-testid={`terms-message-${message.kind}`}
                    className={`p-3 rounded-md text-sm ${message.kind === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}
                >
                    {message.text}
                </div>
            )}

            {/* Publish button row (separate from StickyActionBar's save/reset) */}
            <div className="flex justify-end">
                <button
                    type="button"
                    data-testid="terms-publish-button"
                    onClick={() => setConfirmOpen(true)}
                    disabled={publishing || loading || content.trim().length === 0}
                    className="px-4 py-2 rounded-md bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50"
                >
                    {publishing ? t('Publishing…') : t('Publish new version')}
                </button>
            </div>

                </TabsContent>

                <TabsContent value="acceptance" className="mt-4">
                    <AcceptanceTab />
                </TabsContent>
            </Tabs>

            {/* Confirm publish modal */}
            {confirmOpen && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true">
                    <div className="bg-white rounded-lg max-w-md w-full p-5">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-rose-600 flex-shrink-0" />
                            <div>
                                <h3 className="text-lg font-semibold">{t('Publish new version?')}</h3>
                                <p className="text-sm text-gray-600 mt-2">
                                    {t('This will mark the current draft as the active Terms & Policies document. All employees in the company will be required to re-accept them before continuing to use the system.')}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                type="button"
                                onClick={() => setConfirmOpen(false)}
                                disabled={publishing}
                                className="px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-gray-50"
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                type="button"
                                data-testid="terms-publish-confirm"
                                onClick={handlePublish}
                                disabled={publishing}
                                className="px-4 py-2 rounded-md bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50"
                            >
                                {publishing ? t('Publishing…') : t('Yes, publish')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <StickyActionBar
                hasUnsavedChanges={hasUnsavedChanges}
                isSaving={saving}
                onReset={handleReset}
                onSave={handleSave}
                t={t}
                saveLabel={t('Save draft')}
            />
            </div>
        </PageLayout>
    );
}
